class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      // Skip search inputs — they have their own handlers
      if (event.target.id === 'CollectionSearchInput' || event.target.id === 'CollectionSearchInputMobile') return;
      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    // Restore search input value after DOM replacement
    const activeSearch = typeof collectionSearchQuery !== 'undefined' ? collectionSearchQuery : '';
    const searchEl = document.getElementById('CollectionSearchInput');
    const mobileSearchEl = document.getElementById('CollectionSearchInputMobile');
    if (searchEl) searchEl.value = activeSearch;
    if (mobileSearchEl) mobileSearchEl.value = activeSearch;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains('mobile-facets__details')
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeMax = Number(this.dataset.rangeMax) || 100;
    this.sliderMin = this.querySelector('.price-range-slider__input--min');
    this.sliderMax = this.querySelector('.price-range-slider__input--max');
    this.trackActive = this.querySelector('.price-range-slider__track-active');

    const numberInputs = this.querySelectorAll('input[type="number"]');
    this.inputMin = numberInputs[0];
    this.inputMax = numberInputs[1];

    numberInputs.forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();

    if (this.sliderMin && this.sliderMax) {
      this.sliderMin.addEventListener('input', this.onSliderInput.bind(this));
      this.sliderMax.addEventListener('input', this.onSliderInput.bind(this));
      this.sliderMin.addEventListener('change', this.onSliderChange.bind(this));
      this.sliderMax.addEventListener('change', this.onSliderChange.bind(this));
      this.updateTrack();
    }
  }

  onSliderInput() {
    let minVal = Number(this.sliderMin.value);
    let maxVal = Number(this.sliderMax.value);
    if (minVal > maxVal) {
      [minVal, maxVal] = [maxVal, minVal];
    }
    this.inputMin.value = minVal;
    this.inputMax.value = maxVal;
    this.updateTrack();
  }

  onSliderChange() {
    let minVal = Number(this.sliderMin.value);
    let maxVal = Number(this.sliderMax.value);
    if (minVal > maxVal) {
      const temp = this.sliderMin.value;
      this.sliderMin.value = this.sliderMax.value;
      this.sliderMax.value = temp;
    }
    this.inputMin.value = Math.min(minVal, maxVal);
    this.inputMax.value = Math.max(minVal, maxVal);
    this.updateTrack();
    this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateTrack() {
    if (!this.trackActive || !this.sliderMin || !this.sliderMax) return;
    const min = Math.min(Number(this.sliderMin.value), Number(this.sliderMax.value));
    const max = Math.max(Number(this.sliderMin.value), Number(this.sliderMax.value));
    const rangeMax = Number(this.sliderMin.max) || this.rangeMax;
    const left = (min / rangeMax) * 100;
    const right = 100 - (max / rangeMax) * 100;
    this.trackActive.style.left = left + '%';
    this.trackActive.style.right = right + '%';
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    if (this.sliderMin && this.sliderMax) {
      if (this.inputMin.value) this.sliderMin.value = this.inputMin.value;
      if (this.inputMax.value) this.sliderMax.value = this.inputMax.value;
      this.updateTrack();
    }
  }

  setMinAndMaxValues() {
    if (!this.inputMin || !this.inputMax) return;
    if (this.inputMax.value) this.inputMin.setAttribute('max', this.inputMax.value);
    if (this.inputMin.value) this.inputMax.setAttribute('min', this.inputMin.value);
    if (this.inputMin.value === '') this.inputMax.setAttribute('min', 0);
    if (this.inputMax.value === '') this.inputMin.setAttribute('max', this.inputMax.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

// Collection search — fetches from /search route with section rendering for consistent cards
let searchDebounceTimer;
let collectionSearchQuery = '';
let savedCollectionGrid = null;
let savedCollectionCount = null;

function showSearchLoading() {
  const collection = document.getElementById('ProductGridContainer').querySelector('.collection');
  if (collection) collection.classList.add('loading');
  document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
    .forEach(s => s.classList.remove('hidden'));
}

function hideSearchLoading() {
  const collection = document.getElementById('ProductGridContainer').querySelector('.collection');
  if (collection) collection.classList.remove('loading');
  document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
    .forEach(s => s.classList.add('hidden'));
}

function restoreCollectionGrid() {
  if (!savedCollectionGrid) return;
  const productGrid = document.getElementById('product-grid');
  if (productGrid) productGrid.innerHTML = savedCollectionGrid;
  const countEl = document.getElementById('ProductCountDesktop');
  const countEl2 = document.getElementById('ProductCount');
  if (countEl && savedCollectionCount) countEl.innerHTML = savedCollectionCount;
  if (countEl2 && savedCollectionCount) countEl2.innerHTML = savedCollectionCount;
  const paginationEl = document.querySelector('.pagination-wrapper, .pagination');
  if (paginationEl) paginationEl.style.display = '';
  savedCollectionGrid = null;
  savedCollectionCount = null;
}

function triggerCollectionSearch() {
  const input = document.getElementById('CollectionSearchInput');
  const q = input ? input.value.trim() : '';
  collectionSearchQuery = q;

  if (!q) {
    restoreCollectionGrid();
    hideSearchLoading();
    return;
  }

  // Save original grid on first search
  if (!savedCollectionGrid) {
    const productGrid = document.getElementById('product-grid');
    if (productGrid) savedCollectionGrid = productGrid.innerHTML;
    const countEl = document.getElementById('ProductCountDesktop');
    if (countEl) savedCollectionCount = countEl.innerHTML;
  }

  showSearchLoading();

  // Hide pagination during search
  const paginationEl = document.querySelector('.pagination-wrapper, .pagination');
  if (paginationEl) paginationEl.style.display = 'none';

  fetch(`/search?q=${encodeURIComponent(q)}&type=product&section_id=main-search`)
    .then(r => r.text())
    .then(html => {
      // User may have typed more — only apply if query still matches
      const currentInput = document.getElementById('CollectionSearchInput');
      if (currentInput && currentInput.value.trim() !== q) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const searchGrid = parsed.getElementById('product-grid');
      const productGrid = document.getElementById('product-grid');

      if (searchGrid && productGrid) {
        productGrid.innerHTML = searchGrid.innerHTML;
        // Cancel scroll animations on injected cards
        productGrid.querySelectorAll('.scroll-trigger')
          .forEach(el => el.classList.add('scroll-trigger--cancel'));
      }

      // Update product count
      const searchCount = parsed.getElementById('ProductCount');
      if (searchCount) {
        const countEl = document.getElementById('ProductCountDesktop');
        const countEl2 = document.getElementById('ProductCount');
        if (countEl) countEl.innerHTML = searchCount.innerHTML;
        if (countEl2) countEl2.innerHTML = searchCount.innerHTML;
      }

      hideSearchLoading();
    })
    .catch(() => hideSearchLoading());
}

document.addEventListener('input', e => {
  if (e.target.id !== 'CollectionSearchInput' && e.target.id !== 'CollectionSearchInputMobile') return;
  // Sync both inputs
  const other = e.target.id === 'CollectionSearchInput'
    ? document.getElementById('CollectionSearchInputMobile')
    : document.getElementById('CollectionSearchInput');
  if (other) other.value = e.target.value;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(triggerCollectionSearch, 400);
});

document.addEventListener('keydown', e => {
  if ((e.target.id === 'CollectionSearchInput' || e.target.id === 'CollectionSearchInputMobile') && e.key === 'Enter') {
    e.preventDefault();
  }
});

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
